/**
 * TypeScript enums and interfaces mirroring the Rust protocol definitions.
 * All types correspond to serde-tagged Rust enums in network_message_protocol.rs.
 */

// --- Enums matching Rust ---

export enum BuildingKind {
  SpiritRoot = 'SpiritRoot',
  GatheringArray = 'GatheringArray',
  RefiningArray = 'RefiningArray',
  AlchemyFurnace = 'AlchemyFurnace',
  Forge = 'Forge',
  PuppetWorkshop = 'PuppetWorkshop',
  DefenseTower = 'DefenseTower',
  SwordTower = 'SwordTower',
  SlowTrap = 'SlowTrap',
  PuppetBarracks = 'PuppetBarracks',
  TransporterStation = 'TransporterStation',
  HerbFarm = 'HerbFarm',
}

export enum PuppetKind {
  Transporter = 'Transporter',
  Combat = 'Combat',
}

export enum ResourceKind {
  SpiritQi = 'SpiritQi',
  SpiritStone = 'SpiritStone',
  SpiritWood = 'SpiritWood',
  IceIron = 'IceIron',
  Herb = 'Herb',
  Pill = 'Pill',
  Artifact = 'Artifact',
  Talisman = 'Talisman',
}

export enum TechStage {
  Foundation = 'Foundation',
  GoldenCore = 'GoldenCore',
  NascentSoul = 'NascentSoul',
}

// --- Client → Server messages ---

export type ClientMessage =
  | { type: 'JoinGame'; player_name: string }
  | { type: 'PlaceBuilding'; kind: BuildingKind; x: number; y: number }
  | { type: 'RemoveBuilding'; building_id: number }
  | { type: 'StartResearch'; target_stage: string }
  | { type: 'SetPuppetRoute'; puppet_id: number; target_x: number; target_y: number }
  | { type: 'AttackTarget'; attacker_id: number; target_id: number }
  | { type: 'SetTransporterRoute'; station_building_id: number; source_building_id: number; dest_building_id: number }
  | { type: 'ConsumePill' }
  | { type: 'Ping' };

// --- Server → Client messages ---

export type ServerMessage =
  | { type: 'WorldState' } & WorldSnapshot
  | { type: 'DeltaState'; deltas: EntityDelta[] }
  | { type: 'PlayerJoined'; player: PlayerState }
  | { type: 'PlayerLeft'; player_id: number }
  | { type: 'ResearchProgress'; progress: number; duration: number; target_stage: string }
  | { type: 'ResearchComplete'; new_stage: string }
  | { type: 'DamageDealt'; target_id: number; amount: number; attacker_id: number }
  | { type: 'Error'; message: string }
  | { type: 'Pong' };

// --- Supporting interfaces ---

export interface ProtocolVec2 {
  x: number;
  y: number;
}

export interface BuildingState {
  id: number;
  kind: BuildingKind;
  position: ProtocolVec2;
  hp: number;
  max_hp: number;
  player_id: number;
}

export interface PuppetState {
  id: number;
  kind: PuppetKind;
  position: ProtocolVec2;
  target: ProtocolVec2 | null;
  speed: number;
}

export interface ResourceState {
  kind: ResourceKind;
  position: ProtocolVec2;
  amount: number;
}

export interface PlayerState {
  id: number;
  name: string;
  spirit_qi: number;
  spirit_stone: number;
  herb: number;
  pill: number;
  tech_stage: TechStage;
  research_progress: ResearchProgressState | null;
}

export interface ResearchProgressState {
  target_stage: TechStage;
  progress: number;
  duration: number;
  active: boolean;
}

export interface BuildingUnlockInfo {
  kind: BuildingKind;
  unlocked: boolean;
  required_stage: TechStage;
}

export interface PollutionState {
  position: ProtocolVec2;
  amount: number;
  max: number;
}

export interface WorldSnapshot {
  players: PlayerState[];
  buildings: BuildingState[];
  puppets: PuppetState[];
  resources: ResourceState[];
  pollution: PollutionState[];
  unlock_info: BuildingUnlockInfo[];
}

export interface EntityDelta {
  entity_id: number;
  component: string;
  data: unknown;
}

